import type { Me, WorkspaceRole } from "@unbox/api";
import { LogOut01 } from "@untitledui/icons";
import { SWITCHER_COPY } from "@/components/admin/impersonate/constants";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { getUserInitials } from "@/utils/user-initials";

const MAX_VISIBLE_WORKSPACES = 3;

type Workspace = Me["workspaces"][number];

const roleLabel = (role: WorkspaceRole) => (role === "workspace_admin" ? "Workspace Admin" : "User");

const workspaceLabel = (workspace: Workspace) => `${workspace.name} • ${roleLabel(workspace.role)}`;

const badgeColors: Record<WorkspaceRole, string> = {
  workspace_admin: "bg-purple-900 text-purple-300 ring-purple-800",
  member: "bg-blue-900 text-blue-300 ring-blue-800",
};

const badgeBase =
  "flex size-max items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset";

const WorkspaceBadges = ({ workspaces }: { workspaces: Workspace[] }) => {
  const visible = workspaces.slice(0, MAX_VISIBLE_WORKSPACES);
  const hasOverflow = workspaces.length > MAX_VISIBLE_WORKSPACES;

  return (
    <div className="flex flex-wrap gap-1 px-4" data-testid="impersonated-workspaces">
      {visible.map((workspace) => (
        <span key={workspace.id} className={cx(badgeBase, badgeColors[workspace.role])}>
          {workspaceLabel(workspace)}
        </span>
      ))}
      {hasOverflow && (
        <Tooltip
          placement="top"
          title="Workspaces"
          description={
            <ul className="flex flex-col gap-0.5">
              {workspaces.map((workspace) => (
                <li key={workspace.id}>{workspaceLabel(workspace)}</li>
              ))}
            </ul>
          }
        >
          <TooltipTrigger
            aria-label={`${workspaces.length - visible.length} more workspaces`}
            className={cx(badgeBase, "cursor-default bg-gray-800 text-gray-300 ring-gray-700")}
          >
            ...
          </TooltipTrigger>
        </Tooltip>
      )}
    </div>
  );
};

interface ImpersonatedSessionPanelProps {
  user: Me;
  name: string;
  onEnd: () => void;
}

/** The dark block of the account menu showing who the admin is currently browsing as. */
export const ImpersonatedSessionPanel = ({ user, name, onEnd }: ImpersonatedSessionPanelProps) => (
  <div
    className="flex flex-col gap-0.5 border-t border-gray-800 bg-primary-solid py-2"
    data-testid="impersonated-session"
  >
    <div className="flex items-center gap-2 px-4 py-1.5">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-gray-800 bg-gray-800">
        <span className="text-md font-semibold text-gray-400">{getUserInitials(name)}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-white">{name}</div>
        <div className="truncate text-sm text-gray-400">{user.email}</div>
      </div>
    </div>
    <WorkspaceBadges workspaces={user.workspaces} />
    <div className="px-4 pt-2 pb-1">
      <Button color="primary" size="sm" iconLeading={LogOut01} className="w-full" onClick={onEnd}>
        {SWITCHER_COPY.end}
      </Button>
    </div>
  </div>
);
